// Pure breadcrumb derivation (nav-redesign PR 9).
//
// One integration point: PageLayout renders <Breadcrumbs> from the
// current pathname, so every page gets a consistent "Home › Section ›
// Detail" trail without per-page wiring. JSX-free + testable.

import { PAGE_ROUTE_PATTERNS } from "./route-paths.generated";

export interface Crumb {
  label: string;
  /** When set (and not the last crumb), the crumb is a link. */
  to?: string;
  /**
   * "Admin" has no page of its own (/admin is not a route); it is a menu.
   * The crumb opens that menu for people who have it, and is plain text
   * for anyone who doesn't.
   */
  opensAdminMenu?: boolean;
}

// Friendly labels for known path segments (user-facing vocabulary).
const SEGMENT_LABELS: Record<string, string> = {
  contacts: "Contacts",
  households: "Households",
  opportunities: "Opportunities",
  processing: "Processing",
  "ongoing-care": "Ongoing Care",
  "service-records": "Policies & Services",
  dashboard: "Dashboard",
  "my-day": "My Day",
  // "Firm trends" since the 2026-10-15 navigation overhaul, matching the
  // Home row; "All pages" is the directory at the foot of the rail.
  pulse: "Firm trends",
  "all-pages": "All pages",
  watchlist: "Watchlist",
  trends: "Trends",
  cleanup: "Cleanup",
  "data-needed": "Data Needed",
  reports: "Reports",
  tasks: "Tasks",
  calendar: "Calendar",
  email: "Email",
  automations: "Automations",
  notifications: "Notifications",
  preferences: "Preferences",
  profile: "Profile",
  security: "Security",
  "my-commissions": "My Commissions",
  "my-mortgage-compliance": "My Compliance",
  admin: "Admin",
  ops: "Ops",
  team: "Team",
  compliance: "Compliance",
  integrations: "Integrations",
  quickbooks: "QuickBooks",
  "microsoft-graph": "Office 365",
  ivans: "IVANS",
  vendors: "Vendors",
  forms: "Forms",
  settings: "Settings",
  board: "Board",
  aging: "Aging",
  forecast: "Forecast",
};

// Whether a path is a real page the trail may link to. Every page App.tsx
// declares counts (generated list, so it can't rot the way the old
// hand-kept list of a dozen top-level pages did: Grant, 2026-10-13, could
// not click "Integrations" on the QuickBooks screen). ":param" segments
// match any value, so /households/<id> is a page and links back to the
// household from anything beneath it.
export function isPagePath(path: string): boolean {
  const segs = path.split("/").filter(Boolean);
  return PAGE_ROUTE_PATTERNS.some((pattern) => {
    const pat = pattern.split("/").filter(Boolean);
    return pat.length === segs.length && pat.every((p, i) => p.startsWith(":") || p === segs[i]);
  });
}

// An opaque record id (uuid / long hex / all-digits) — rendered as
// "Detail" rather than the raw id.
export function isIdLikeSegment(seg: string): boolean {
  // A year-month ("202611" / "2026-11") is a date, not a record id.
  if (monthName(seg)) return false;
  if (/^\d+$/.test(seg)) return true;
  if (/^[0-9a-f]{6,}$/i.test(seg)) return true;
  if (/^[0-9a-f]{8,}-[0-9a-f-]+$/i.test(seg)) return true;
  return false;
}

/** "202611" or "2026-11" → "November 2026"; anything else → null. */
export function monthName(seg: string): string | null {
  const m = /^(20\d{2})-?(0[1-9]|1[0-2])$/.exec(seg);
  if (!m) return null;
  const date = new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, 1));
  return date.toLocaleDateString("en-US", { month: "long", year: "numeric", timeZone: "UTC" });
}

export function labelForSegment(seg: string): string {
  const known = SEGMENT_LABELS[seg];
  if (known) return known;
  const month = monthName(seg);
  if (month) return month;
  // Title-case fallback for unknown segments ("loan-files" → "Loan Files",
  // "financial_initial_consultation" → "Financial Initial Consultation").
  return seg
    .split(/[-_]/)
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");
}

export interface BreadcrumbOptions {
  /**
   * The current page's own name for the record it shows ("Smith
   * Household"). Replaces "Detail" on the trail's last record step.
   */
  currentName?: string | null;
}

// Build the breadcrumb trail for a pathname. Returns [] for the home
// route (no trail needed). The first crumb is always Home; intermediate
// crumbs link when their accumulated path is a real page; the last crumb
// is the current page (never a link).
export function breadcrumbForPath(pathname: string, options: BreadcrumbOptions = {}): Crumb[] {
  const segs = pathname.split("/").filter(Boolean);
  if (segs.length === 0) return [];
  const crumbs: Crumb[] = [{ label: "Home", to: "/" }];
  // Which step the page's own name belongs to: the LAST record id in the
  // address (/households/<id>/companies/insurance names the household).
  let lastIdIndex = -1;
  segs.forEach((seg, i) => {
    if (isIdLikeSegment(seg)) lastIdIndex = i;
  });
  let acc = "";
  segs.forEach((seg, i) => {
    acc += "/" + seg;
    const isLast = i === segs.length - 1;
    const isId = isIdLikeSegment(seg);
    const label = isId
      ? (i === lastIdIndex && options.currentName) || "Detail"
      : labelForSegment(seg);
    if (!isLast && acc === "/admin") {
      crumbs.push({ label, opensAdminMenu: true });
    } else if (!isLast && isPagePath(acc)) {
      crumbs.push({ label, to: acc });
    } else {
      crumbs.push({ label });
    }
  });
  return crumbs;
}

/**
 * What a "← Back to …" link calls the page you came from: its own name
 * when it told us one (a record), otherwise the last step of its trail
 * ("Integrations", "Opportunities"). An unnamed record reads "previous
 * page" rather than the meaningless "Detail".
 */
export function labelForPreviousPage(pathname: string, name?: string | null): string {
  if (name && name.trim()) return name.trim();
  const last = breadcrumbForPath(pathname).at(-1)?.label;
  if (!last) return "Home";
  return last === "Detail" ? "previous page" : last;
}
